// THE LIVE CHECK'S VERDICT — the one structured answer a `live_check` run gives (plan
// 101-multi-repo-goal-legs D6), shared by the box that answers it, the tool the model answers
// through and the mothership that audits it.
//
// THREE VERDICTS, the house grader's vocabulary (`docs/standards/pr-body.md` § Live test):
// `pass` (the acceptance holds on the deployed system), `fail` (the system answered and the
// acceptance does not hold) and `cannot_grade` (the acceptance could not run). The verdict is
// a TOOL CALL's input, never parsed from prose: a model that narrates "looks good" and calls
// nothing has given no verdict, and no verdict grades `cannot_grade`, never `pass`.
//
// EVIDENCE IS REQUIRED, and it is a body, not a status: `CLAUDE.md` § "A 200 is not an
// answer" — each observation carries what was probed, the status, the content type and an
// excerpt of the BODY it read, plus which model served when the probe asked one. Bounded,
// because the payload rides a run event and an audit row; the bounds name that, not a cost.

import { z } from 'zod';

/** The verdicts, in the house grader's words. */
export const LiveCheckVerdict = {
  Pass: 'pass',
  Fail: 'fail',
  CannotGrade: 'cannot_grade',
} as const;
export const LiveCheckVerdictSchema = z.enum(LiveCheckVerdict);
export type LiveCheckVerdict = z.infer<typeof LiveCheckVerdictSchema>;

/** Chars of one body excerpt: enough to show the field the acceptance names, not a page. */
export const LIVE_CHECK_BODY_EXCERPT_MAX_CHARS = 2_000;
/** Observations one verdict carries: one per acceptance step, and a goal's live test is short. */
export const LIVE_CHECK_MAX_OBSERVATIONS = 20;
/** Chars of the verdict's summary line(s). */
export const LIVE_CHECK_SUMMARY_MAX_CHARS = 2_000;

/** One thing the check probed and what it READ back. */
export const LiveCheckObservationSchema = z.object({
  target: z
    .string()
    .trim()
    .min(1)
    .max(500)
    .describe('What was probed: the URL, command or query, exactly as run.'),
  status: z
    .string()
    .trim()
    .max(100)
    .nullable()
    .describe('The status it answered (an HTTP status, an exit code), or null when none applies.'),
  content_type: z
    .string()
    .trim()
    .max(200)
    .nullable()
    .describe('The content type of the answer, or null when none applies.'),
  body_excerpt: z
    .string()
    .max(LIVE_CHECK_BODY_EXCERPT_MAX_CHARS)
    .describe('An excerpt of the BODY or output read, including the part the acceptance names.'),
  served_by: z
    .string()
    .trim()
    .max(200)
    .nullable()
    .describe('Which model served the answer, when the probe asked a model; else null.'),
});
export type LiveCheckObservation = z.infer<typeof LiveCheckObservationSchema>;

/** The whole verdict: what the tool takes and the status event carries. */
export const LiveCheckAnswerSchema = z.object({
  verdict: LiveCheckVerdictSchema.describe(
    'pass: every acceptance step holds on the deployed system. fail: the system answered and a step does not hold. cannot_grade: a step could not run (a broken step, no access, the system unreachable).',
  ),
  summary: z
    .string()
    .trim()
    .min(1)
    .max(LIVE_CHECK_SUMMARY_MAX_CHARS)
    .describe('Which acceptance steps held, which did not, and why, in a few sentences.'),
  observations: z
    .array(LiveCheckObservationSchema)
    .max(LIVE_CHECK_MAX_OBSERVATIONS)
    .describe('What was probed and read, one entry per step. Required for pass and fail.'),
});
export type LiveCheckAnswer = z.infer<typeof LiveCheckAnswerSchema>;

/** The `status` run event a live check emits once, carrying its answer. */
export const LIVE_CHECK_VERDICT_EVENT = 'live_check_verdict' as const;

/** That event's payload: the event name beside the answer. */
export const LiveCheckVerdictPayloadSchema = LiveCheckAnswerSchema.extend({
  event: z.literal(LIVE_CHECK_VERDICT_EVENT),
});
export type LiveCheckVerdictPayload = z.infer<typeof LiveCheckVerdictPayloadSchema>;

/**
 * A `pass` or `fail` with no observation proves nothing: grade it `cannot_grade`. Pure. A
 * `cannot_grade` stands as given — the check could not run, so it may have read nothing.
 */
export function gradedLiveCheckVerdict(answer: LiveCheckAnswer): LiveCheckVerdict {
  if (answer.verdict !== LiveCheckVerdict.CannotGrade && answer.observations.length === 0) {
    return LiveCheckVerdict.CannotGrade;
  }
  return answer.verdict;
}
